import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isChunkLoadError } from '@/lib/chunkReload';
import tapstarLogo from '@/img/tapstar_logo.png';

/**
 * Fallback que renderiza la Sentry ErrorBoundary cuando un componente React
 * tira una excepción no-capturada (incl. fallos de carga de chunks lazy tras
 * un deploy). Sin esto, la SPA se quedaría en blanco.
 *
 * IMPORTANTE: este componente NO debe depender de ningún Context (Language,
 * Auth, QueryClient...). El ErrorBoundary vive en main.jsx envolviendo <App/>,
 * así que cuando renderiza se ejecuta FUERA de todos los providers. Si usara
 * useLanguage() crashearía con "must be used within a Provider" → pantalla
 * blanca otra vez, anulando el propósito del fallback. Por eso los textos van
 * hardcoded con detección de idioma vía navigator.language (sin Context).
 *
 * Estilo alineado al branding de Tapstar (mismo lenguaje visual que Login:
 * card blanca rounded-3xl, logo, acento #feae2c). El logo es un asset estático
 * del bundle principal — si faltara (deploy mismatch) cae al alt text.
 *
 * Decisiones §40 item D.
 */
const COPY = {
  es: {
    title: 'Algo ha salido mal',
    body: 'Hemos registrado el error y lo revisaremos. Puedes reintentar o volver al inicio.',
    retry: 'Reintentar',
    home: 'Ir al inicio',
  },
  en: {
    title: 'Something went wrong',
    body: "We've logged the error and will look into it. You can retry or go back home.",
    retry: 'Try again',
    home: 'Go home',
  },
  pt: {
    title: 'Algo deu errado',
    body: 'Registamos o erro e vamos investigá-lo. Pode tentar de novo ou voltar ao início.',
    retry: 'Tentar de novo',
    home: 'Ir para o início',
  },
  de: {
    title: 'Etwas ist schiefgelaufen',
    body: 'Wir haben den Fehler protokolliert und kümmern uns darum. Sie können es erneut versuchen oder zur Startseite zurückkehren.',
    retry: 'Erneut versuchen',
    home: 'Zur Startseite',
  },
  fr: {
    title: 'Une erreur est survenue',
    body: "Nous avons enregistré l'erreur et allons l'examiner. Vous pouvez réessayer ou revenir à l'accueil.",
    retry: 'Réessayer',
    home: "Aller à l'accueil",
  },
};

function detectLang() {
  // navigator.language ej. 'es-ES' → 'es'. Sin Context, a prueba de crashes.
  const raw = (typeof navigator !== 'undefined' && navigator.language) || 'es';
  const code = raw.slice(0, 2).toLowerCase();
  return COPY[code] ? code : 'es';
}

export default function ErrorFallback({ error, resetError }) {
  const t = COPY[detectLang()];
  // Para un chunk-error, resetError re-renderiza el MISMO árbol fallido → 404
  // otra vez (bucle). Un reload duro trae el index.html nuevo con los hashes
  // correctos, así que es la única recuperación real. Lo inicia el usuario, así
  // que no hay riesgo de auto-loop (a diferencia del reload automático, que sí
  // lleva guarda). Para el resto de errores, resetError sigue siendo lo mejor
  // (no pierde estado).
  const onRetry = isChunkLoadError(error)
    ? () => window.location.reload()
    : resetError;
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md border border-slate-200 shadow-2xl bg-white rounded-3xl overflow-hidden">
        <div className="px-8 pt-10 pb-8 text-center space-y-6">
          <div className="flex justify-center">
            <img src={tapstarLogo} alt="Tapstar" className="h-14 w-auto object-contain" />
          </div>

          <div className="flex justify-center">
            <span className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#feae2c]/10">
              <AlertTriangle className="h-7 w-7 text-[#feae2c]" />
            </span>
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t.title}</h1>
            <p className="text-slate-500 text-base">{t.body}</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
            <Button
              onClick={onRetry}
              className="bg-[#feae2c] hover:bg-[#ffc04d] text-black font-bold gap-2 rounded-xl h-12 px-6 shadow-md transition-all active:scale-95"
            >
              <RefreshCw className="w-4 h-4" />
              {t.retry}
            </Button>
            <Button
              onClick={() => (window.location.href = '/')}
              variant="outline"
              className="rounded-xl h-12 px-6 border-slate-200 font-semibold"
            >
              {t.home}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
